// Évaluations — shared vocabulary: formatting, status pills, note
// chips, radar and the criteria form. Ported from the design
// prototype (ev-form.jsx / ev-data.jsx helpers); the criteria
// template + math live in @pointage/shared.
import { useEffect, useMemo, useRef, useState } from 'react';
import {
  EVAL_BILAN_QS, EVAL_SCALE, EVAL_SCALE_LBL,
  evalBlockAvg, evalRadarAxes, evalTemplate, evalTemplateCrits, evalWeighted,
  type EvalEmployeeDTO, type EvalNotes, type EvalStep,
} from '@pointage/shared';
import { EmpCell } from '../conges/common.js';
import { useAutosaveFlush } from '../../lib/unsaved.js';

// ── formatting ───────────────────────────────────────────────
export const evNum = (v: number) =>
  (Math.round(v * 100) / 100).toFixed(2).replace('.', ',').replace(/,?0+$/, '').replace(/,$/, '') || '0';
export const evN1 = (v: number | null | undefined) => (v == null ? '—' : String(v).replace('.', ','));
export const evFmtYmd = (s: string | null | undefined) => (s ? s.split('-').reverse().join('/') : '—');

export const EV_STEP_META: Record<EvalStep, { lbl: string; c: string; bg: string }> = {
  none: { lbl: 'Non commencée', c: 'var(--ink3)', bg: 'var(--s2)' },
  draft: { lbl: 'Brouillon en cours', c: 'var(--warnc)', bg: 'var(--warns)' },
  partial: { lbl: "En attente de l'autre partie", c: 'var(--infoc)', bg: 'var(--infos)' },
  gap: { lbl: 'Prête pour comparaison', c: 'var(--shf)', bg: 'var(--shfs)' },
  oto: { lbl: '1:1 planifié', c: 'var(--shf)', bg: 'var(--shfs)' },
  done: { lbl: 'Clôturée & signée', c: 'var(--greenc)', bg: 'var(--greens)' },
};

export function EvStPill({ step }: { step: EvalStep }) {
  const s = EV_STEP_META[step];
  return <span className="ev-st" style={{ color: s.c, background: s.bg }}><i />{s.lbl}</span>;
}

export function EvEmpCell({ emp }: { emp: EvalEmployeeDTO }) {
  return <EmpCell emp={{ id: emp.id, name: emp.name, role: emp.department, entityId: emp.entityId, code: emp.code }} />;
}

// ── note chips ───────────────────────────────────────────────
export function EvNoteChips({ value, onPick, disabled, compact }: {
  value: number | null | undefined;
  onPick?: (v: number | null) => void;
  disabled?: boolean;
  compact?: boolean;
}) {
  return (
    <div className="ev-chips">
      {EVAL_SCALE.map((v) => (
        <button key={v} type="button" disabled={disabled} title={EVAL_SCALE_LBL[v]}
          className={'ev-chip' + (value === v ? ' on' : '') + (compact ? ' ghost' : '')}
          onClick={onPick ? () => onPick(value === v ? null : v) : undefined}
          style={compact ? { width: 28, height: 26, fontSize: 10 } : undefined}>
          {evN1(v)}
        </button>
      ))}
    </div>
  );
}

export function EvScaleLegend() {
  return (
    <div className="ev-scale-leg">
      {EVAL_SCALE.map((v) => <span key={v}><b>{evN1(v)}</b>{EVAL_SCALE_LBL[v]}</span>)}
    </div>
  );
}

// ── radar auto vs manager (highest-coefficient criteria) ─────
export function EvRadar({ auto, mgr, tplCode, size = 280 }: { auto?: EvalNotes | null; mgr?: EvalNotes | null; tplCode: string; size?: number }) {
  const axes = evalRadarAxes(tplCode);
  const cx = size / 2, cy = size / 2, R = size / 2 - 34, n = axes.length;
  const pt = (i: number, v: number): [number, number] => {
    const a = -Math.PI / 2 + (i * 2 * Math.PI) / n;
    const r = R * (v / 5);
    return [cx + r * Math.cos(a), cy + r * Math.sin(a)];
  };
  const ring = (f: number) => axes.map((_, i) => pt(i, 5 * f).join(',')).join(' ');
  const poly = (notes: EvalNotes) => axes.map((c, i) => pt(i, notes[c.id] ?? 0).join(',')).join(' ');
  return (
    <div className="ev-radar-wrap">
      <svg className="ev-radar" width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
        {[0.25, 0.5, 0.75, 1].map((f) => <polygon key={f} className="grid-l" points={ring(f)} strokeWidth={f === 1 ? 1.3 : 0.7} />)}
        {axes.map((c, i) => {
          const [x, y] = pt(i, 5);
          return <line key={c.id} x1={cx} y1={cy} x2={x} y2={y} stroke="var(--line)" strokeWidth=".7" />;
        })}
        {mgr && <polygon className="poly-m" points={poly(mgr)} />}
        {auto && <polygon className="poly-a" points={poly(auto)} />}
        {axes.map((c, i) => {
          const [x, y] = pt(i, 6.1);
          return <text key={c.id} x={x} y={y} textAnchor="middle" dominantBaseline="middle">{c.short}</text>;
        })}
      </svg>
      <div className="ev-legend">
        {auto && <span><i style={{ background: 'var(--shf)' }} />Auto-évaluation</span>}
        {mgr && <span><i style={{ background: 'var(--info)' }} />Manager</span>}
      </div>
    </div>
  );
}

// ── criteria form (auto & manager) ───────────────────────────
// Local-state editor: initializes from the DTO, autosaves (debounced)
// through onSave, submits through onSubmit. Remount (key by employee +
// role) when the underlying evaluation changes hands.
export interface EvalFormProps {
  role: 'auto' | 'mgr';
  /** Criteria-template code of the evaluation being edited. */
  tplCode: string;
  notes: EvalNotes;
  bilan?: string[]; // auto only
  comment?: string; // mgr only
  readOnly?: boolean;
  submittedAt?: string | null;
  deadline: string; // YYYY-MM-DD
  saving?: boolean;
  onSave?: (data: { notes: EvalNotes; bilan: string[]; comment: string }) => void;
  onSubmit?: () => void;
}

export function EvEvalForm({ role, tplCode, notes: notes0, bilan: bilan0, comment: comment0, readOnly, submittedAt, deadline, saving, onSave, onSubmit }: EvalFormProps) {
  const tpl = evalTemplate(tplCode);
  const crits = useMemo(() => evalTemplateCrits(tplCode), [tplCode]);
  const [notes, setNotes] = useState<EvalNotes>(() => ({ ...notes0 }));
  const [bilan, setBilan] = useState<string[]>(() => {
    const b = [...(bilan0 ?? [])];
    while (b.length < 5) b.push('');
    return b;
  });
  const [comment, setComment] = useState(comment0 ?? '');
  const dirty = useRef(false);
  const latest = useRef({ notes, bilan, comment });
  latest.current = { notes, bilan, comment };

  // Debounced autosave.
  useEffect(() => {
    if (readOnly || !onSave || !dirty.current) return;
    const id = setTimeout(() => { dirty.current = false; onSave(latest.current); }, 700);
    return () => clearTimeout(id);
  }, [notes, bilan, comment, readOnly, onSave]);
  // Ce qui attend encore ses 700 ms part tout de suite quand on quitte.
  useAutosaveFlush(`eval-form-${tplCode}-${role}`, () => {
    if (readOnly || !onSave || !dirty.current) return;
    dirty.current = false;
    onSave(latest.current);
  });

  const setNote = (critId: string, v: number | null) => {
    dirty.current = true;
    setNotes((s) => {
      const next = { ...s };
      if (v == null) delete next[critId];
      else next[critId] = v;
      return next;
    });
  };

  const W = useMemo(() => evalWeighted(notes, crits), [notes, crits]);

  const submit = () => {
    if (!onSubmit) return;
    // Flush any pending edit before submitting.
    if (dirty.current && onSave) { dirty.current = false; onSave(latest.current); }
    onSubmit();
  };

  return (
    <div>
      <EvScaleLegend />
      {tpl.blocks.map((b, bi) => {
        const avg = evalBlockAvg(notes, b.id, crits);
        return (
          <div key={b.id}>
            <div className="ev-blockh">
              <span className="n">0{bi + 1}</span>
              <h4>{b.lbl}</h4>
              <span className="avg">moy. pondérée <b>{avg != null ? evNum(avg) : '—'}</b>/5</span>
            </div>
            {b.crits.map((c, i) => {
              const v = notes[c.id];
              return (
                <div key={c.id} className="ev-crit" style={{ animationDelay: `${i * 0.03}s` }}>
                  <div>
                    <div className="nm">{c.lbl}<span className="cf">×{evN1(c.coef)}</span></div>
                    <div className="ds">{c.ds}</div>
                    <details className="ev-beh">
                      <summary>Comportements associés</summary>
                      <ul>{c.beh.map((x, j) => <li key={j}>{x}</li>)}</ul>
                    </details>
                  </div>
                  <EvNoteChips value={v} disabled={readOnly} onPick={(val) => setNote(c.id, val)} />
                  <div className="ev-pts">{v != null ? <><b>{evNum(v * c.coef)}</b> pts</> : '· · ·'}</div>
                </div>
              );
            })}
          </div>
        );
      })}

      <div className="ev-blockh">
        <span className="n">05</span>
        <h4>{role === 'auto' ? 'Bilan qualitatif' : 'Commentaire général du manager'}</h4>
      </div>
      {role === 'auto'
        ? EVAL_BILAN_QS.map((q, i) => (
          <div key={i} className="ev-bilan-q">
            <label>{i + 1}. {q}</label>
            {readOnly
              ? <div className="ev-ro">{bilan[i] || '—'}</div>
              : <textarea className="ev-ta" value={bilan[i]} placeholder="Votre réponse…"
                  onChange={(e) => { dirty.current = true; setBilan((s) => s.map((x, j) => (j === i ? e.target.value : x))); }} />}
          </div>
        ))
        : (readOnly
          ? <div className="ev-ro">{comment || '—'}</div>
          : <textarea className="ev-ta" style={{ minHeight: 100 }} value={comment}
              placeholder="Synthèse de l'année, axes de progrès, message à l'employé…"
              onChange={(e) => { dirty.current = true; setComment(e.target.value); }} />)}

      <div className="ev-formfoot">
        <div>
          <div className="gn">{evNum(W.note)}<i> /5</i></div>
          <div className="lb">Note globale pondérée</div>
        </div>
        <div style={{ flex: 1 }}>
          <div className="lb" style={{ marginBottom: 5 }}>
            {W.filled}/{W.total} critères notés · {readOnly
              ? `soumise le ${evFmtYmd(submittedAt)}`
              : `échéance ${evFmtYmd(deadline)}${saving ? ' · enregistrement…' : ''}`}
          </div>
          <div className="ev-prog"><i style={{ width: `${W.pct}%` }} /></div>
        </div>
        {!readOnly && (
          <button className="gm-attn-go" disabled={W.filled < W.total} onClick={submit}>
            {W.filled < W.total
              ? `Encore ${W.total - W.filled} critère${W.total - W.filled > 1 ? 's' : ''}`
              : role === 'auto' ? 'Soumettre mon auto-évaluation' : 'Soumettre mon évaluation'}
          </button>
        )}
        {readOnly && <span className="ev-st" style={{ color: 'var(--greenc)', background: 'var(--greens)' }}><i />Soumise</span>}
      </div>
    </div>
  );
}
